import { useRef, useState } from 'react';
import { GridElement, Connection, ElementAction } from '../../types';

// Types from the original component
interface GeoJSONFeature {
  type: 'Feature';
  properties: Record<string, unknown>;
  geometry: {
    type: 'Point' | 'LineString' | 'Polygon' | 'MultiPoint' | 'MultiLineString' | 'MultiPolygon';
    coordinates: number[] | number[][] | number[][][];
  };
  id?: string | number;
}

interface GeoJSONData {
  type: 'FeatureCollection' | 'Feature';
  features?: GeoJSONFeature[];
  properties?: Record<string, unknown>;
  geometry?: GeoJSONFeature['geometry'];
}

export interface GeoJSONLayer {
  id: string;
  name: string;
  data: GeoJSONData;
  visible: boolean;
  color: string;
  opacity: number;
}

// Presenter state and actions
export interface GeoJSONImporterState {
  showPointConverter: boolean;
  showLineStringConverter: boolean;
  selectedFile: {geoJSON: GeoJSONData; fileName: string} | null;
  pointMapping: {[key: string]: string};
  lineStringMapping: {[key: string]: string};
  radializeNetwork: boolean;
}

export interface GeoJSONImporterActions {
  handleFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  convertPointsToElements: () => void;
  convertLineStringsToConnections: () => void;
  convertLineStringsToConnectionsAutomatically: (lineStrings: any[]) => void;
  testConnectionCreation: () => void;
  cancelConversion: () => void;
  setPointMapping: (mapping: {[key: string]: string}) => void;
  setLineStringMapping: (mapping: {[key: string]: string}) => void;
  setRadializeNetwork: (value: boolean) => void;
  triggerFileInput: () => void;
}

interface GeoJSONImporterProps {
  layers: GeoJSONLayer[];
  onLayerAdd: (layer: GeoJSONLayer) => void;
  onLayerRemove: (layerId: string) => void;
  onLayerUpdate: (layerId: string, updates: Partial<GeoJSONLayer>) => void;
  onElementsAdd?: (elements: GridElement[]) => void;
  onConnectionsAdd?: (connections: Connection[]) => void;
  existingElements?: GridElement[]; // Éléments existants pour créer des connexions
  mapBounds?: {
    minLat: number;
    maxLat: number;
    minLng: number;
    maxLng: number;
  };
}

export const useGeoJSONImporterPresenter = ({
  onLayerAdd,
  onElementsAdd,
  onConnectionsAdd,
  existingElements = [],
  mapBounds = { minLat: 48.86, maxLat: 48.88, minLng: 2.29, maxLng: 2.31 }
}: Pick<GeoJSONImporterProps, 'onLayerAdd' | 'onElementsAdd' | 'onConnectionsAdd' | 'existingElements' | 'mapBounds'>) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showPointConverter, setShowPointConverter] = useState(false);
  const [showLineStringConverter, setShowLineStringConverter] = useState(false);
  const [selectedFile, setSelectedFile] = useState<{geoJSON: GeoJSONData; fileName: string} | null>(null);
  const [pointMapping, setPointMapping] = useState<{[key: string]: string}>({});
  const [lineStringMapping, setLineStringMapping] = useState<{[key: string]: string}>({});
  // Réseau radial: ouvrir les boucles (arbre couvrant) à l'import. Un réseau BT
  // est normalement radial; on coupe le câble le plus long de chaque boucle.
  const [radializeNetwork, setRadializeNetwork] = useState(true);

  const extractPoints = (geoJSON: GeoJSONData): GeoJSONFeature[] => {
    const points: GeoJSONFeature[] = [];
    
    if (geoJSON.type === 'FeatureCollection' && geoJSON.features) {
      geoJSON.features.forEach((feature) => {
        if (feature.geometry && feature.geometry.type === 'Point') {
          points.push(feature);
        }
      });
    } else if (geoJSON.type === 'Feature' && geoJSON.geometry && geoJSON.geometry.type === 'Point') {
      points.push(geoJSON as GeoJSONFeature);
    }
    
    return points;
  };

  const extractLineStrings = (geoJSON: GeoJSONData): GeoJSONFeature[] => {
    const lineStrings: GeoJSONFeature[] = [];
    
    if (geoJSON.type === 'FeatureCollection' && geoJSON.features) {
      geoJSON.features.forEach((feature) => {
        if (feature.geometry && feature.geometry.type === 'LineString') {
          lineStrings.push(feature);
        }
      });
    } else if (geoJSON.type === 'Feature' && geoJSON.geometry && geoJSON.geometry.type === 'LineString') {
      lineStrings.push(geoJSON as GeoJSONFeature);
    }
    
    return lineStrings;
  };

  



  const generateRandomColor = (): string => {
    const colors = [
      '#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16',
      '#22c55e', '#10b981', '#14b8a6', '#06b6d4', '#0ea5e9',
      '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7', '#d946ef',
      '#ec4899', '#f43f5e'
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  const addAsLayer = (geoJSON: GeoJSONData, fileName: string) => {
    const newLayer: GeoJSONLayer = {
      id: `layer_${Date.now()}`,
      name: fileName.replace('.geojson', '').replace('.json', ''),
      data: geoJSON,
      visible: true,
      color: generateRandomColor(),
      opacity: 0.7
    };

    onLayerAdd(newLayer);
  };

  const geoToGridCoordinates = (lng: number, lat: number) => {
    // Conversion des coordonnées géographiques en coordonnées de grille (1200x800)
    const width = 1200;
    const height = 800;
    
    const x = ((lng - mapBounds.minLng) / (mapBounds.maxLng - mapBounds.minLng)) * width;
    const y = height - ((lat - mapBounds.minLat) / (mapBounds.maxLat - mapBounds.minLat)) * height;
    
    return { x, y };
  };

  const calculateDistance = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
    const R = 6371; // Rayon de la Terre en km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c; // Distance en km
  };

  /**
   * Calcule la tolérance optimale basée sur la densité des points
   * @param points - Liste des points GeoJSON
   * @returns Tolérance optimale en degrés
   */
  const calculateOptimalTolerance = (points: GeoJSONFeature[]): number => {
    if (points.length <= 1) return 0.0001; // Tolérance par défaut

    // Calculer les distances moyennes entre les points les plus proches
    const distances: number[] = [];
    
    for (let i = 0; i < Math.min(points.length, 100); i++) { // Limiter à 100 points pour la performance
      const coords1 = points[i].geometry.coordinates as number[];
      let minDistance = Infinity;
      
      for (let j = 0; j < points.length; j++) {
        if (i === j) continue;
        
        const coords2 = points[j].geometry.coordinates as number[];
        const distance = calculateDistance(coords1[1], coords1[0], coords2[1], coords2[0]);
        
        if (distance < minDistance) {
          minDistance = distance;
        }
      }
      
      if (minDistance !== Infinity) {
        distances.push(minDistance);
      }
    }

    if (distances.length === 0) return 0.0001;

    // Calculer la distance médiane
    distances.sort((a, b) => a - b);
    const medianDistance = distances[Math.floor(distances.length / 2)];

    // Convertir la distance en degrés (approximation)
    // 1 degré ≈ 111 km
    const toleranceInDegrees = medianDistance / 111000;

    // Limiter la tolérance entre 0.00001 (1m) et 0.001 (111m)
    const minTolerance = 0.00001; // ~1m
    const maxTolerance = 0.001;   // ~111m
    
    const optimalTolerance = Math.max(minTolerance, Math.min(maxTolerance, toleranceInDegrees));
    
    console.log(`🎯 Tolérance optimale calculée: ${optimalTolerance.toFixed(6)}° (~${Math.round(optimalTolerance * 111000)}m)`);
    console.log(`📊 Basée sur ${distances.length} distances, médiane: ${medianDistance.toFixed(2)}m`);
    
    return optimalTolerance;
  };

  /**
   * Fusionne intelligemment les propriétés de plusieurs points
   * @param points - Liste des points à fusionner
   * @returns Propriétés fusionnées optimisées
   */
  const mergePropertiesIntelligently = (points: GeoJSONFeature[]): Record<string, unknown> => {
    const mergedProperties: Record<string, unknown> = {};
    const propertyGroups: Record<string, unknown[]> = {};
    
    // Grouper les propriétés par clé
    points.forEach(point => {
      if (point.properties) {
        Object.entries(point.properties).forEach(([key, value]) => {
          if (value !== null && value !== undefined) {
            if (!propertyGroups[key]) {
              propertyGroups[key] = [];
            }
            propertyGroups[key].push(value);
          }
        });
      }
    });
    
    // Traiter chaque groupe de propriétés
    Object.entries(propertyGroups).forEach(([key, values]) => {
      const uniqueValues = Array.from(new Set(values));
      
      switch (key) {
        case 'name':
          // Fusionner les noms de manière intelligente
          const names = uniqueValues.map(v => String(v).trim()).filter(n => n.length > 0);
          if (names.length === 1) {
            mergedProperties.name = names[0];
          } else if (names.length > 1) {
            mergedProperties.name = names.join(' + ');
            mergedProperties.originalNames = names;
          }
          break;
          
        case 'type':
          // Déterminer le type principal avec priorité
          const typePriority = { 
            'power-plant': 5, 'generator': 5,
            'substation': 4, 'sub-station': 4,
            'transformer': 3, 'transformateur': 3,
            'meter': 2, 'compteur': 2,
            'pole': 1, 'poteau': 1,
            'junction': 1.5, 'jonction': 1.5,
            'switch': 2.5, 'interrupteur': 2.5
          };
          
          let mainType = 'pole';
          let maxPriority = 0;
          
          uniqueValues.forEach(type => {
            const priority = typePriority[String(type).toLowerCase() as keyof typeof typePriority] || 0;
            if (priority > maxPriority) {
              maxPriority = priority;
              mainType = String(type);
            }
          });
          
          mergedProperties.type = mainType;
          if (uniqueValues.length > 1) {
            mergedProperties.originalTypes = uniqueValues;
          }
          break;
          
        case 'voltage':
          // Calculer la tension moyenne ou garder la plus élevée
          const voltages = uniqueValues.filter(v => typeof v === 'number' || !isNaN(Number(v)))
            .map(v => Number(v));
          
          if (voltages.length > 0) {
            if (voltages.length === 1) {
              mergedProperties.voltage = voltages[0];
            } else {
              const avgVoltage = Math.round(voltages.reduce((a, b) => a + b, 0) / voltages.length);
              mergedProperties.voltage = avgVoltage;
              mergedProperties.voltageRange = `${Math.min(...voltages)}-${Math.max(...voltages)}V`;
              mergedProperties.originalVoltages = voltages;
            }
          }
          break;
          
        case 'power':
        case 'capacity':
          // Additionner les puissances/capacités
          const powers = uniqueValues.filter(v => typeof v === 'number' || !isNaN(Number(v)))
            .map(v => Number(v));
          
          if (powers.length > 0) {
            const totalPower = powers.reduce((a, b) => a + b, 0);
            mergedProperties[key] = totalPower;
            if (powers.length > 1) {
              mergedProperties[`${key}Details`] = `${powers.join(' + ')} = ${totalPower}`;
              mergedProperties[`original${key.charAt(0).toUpperCase() + key.slice(1)}s`] = powers;
            }
          }
          break;
          
        case 'description':
          // Fusionner les descriptions
          const descriptions = uniqueValues.map(v => String(v).trim()).filter(d => d.length > 0);
          if (descriptions.length === 1) {
            mergedProperties.description = descriptions[0];
          } else if (descriptions.length > 1) {
            mergedProperties.description = descriptions.join(' | ');
            mergedProperties.originalDescriptions = descriptions;
          }
          break;
          
        case 'status':
        case 'state':
          // Prendre le statut le plus récent ou le plus important
          const statusPriority = { 'active': 3, 'enabled': 3, 'on': 3, 'working': 3, 'ok': 2, 'maintenance': 1, 'disabled': 0, 'off': 0 };
          let mainStatus = 'unknown';
          let maxStatusPriority = -1;
          
          uniqueValues.forEach(status => {
            const priority = statusPriority[String(status).toLowerCase() as keyof typeof statusPriority] ?? 0;
            if (priority > maxStatusPriority) {
              maxStatusPriority = priority;
              mainStatus = String(status);
            }
          });
          
          mergedProperties[key] = mainStatus;
          if (uniqueValues.length > 1) {
            mergedProperties[`original${key.charAt(0).toUpperCase() + key.slice(1)}s`] = uniqueValues;
          }
          break;
          
        case 'id':
        case 'uuid':
          // Garder tous les IDs pour traçabilité
          mergedProperties[key] = uniqueValues[0]; // Premier ID comme principal
          if (uniqueValues.length > 1) {
            mergedProperties[`all${key.charAt(0).toUpperCase() + key.slice(1)}s`] = uniqueValues;
          }
          break;
          
        default:
          // Pour les autres propriétés, prendre la première valeur non vide
          if (!mergedProperties[key]) {
            mergedProperties[key] = uniqueValues[0];
          }
          if (uniqueValues.length > 1) {
            mergedProperties[`original${key.charAt(0).toUpperCase() + key.slice(1)}s`] = uniqueValues;
          }
      }
    });
    
    // Ajouter des métadonnées de fusion
    mergedProperties.mergedFrom = points.length;
    mergedProperties.mergedAt = new Date().toISOString();
    mergedProperties.originalCount = points.length;
    
    return mergedProperties;
  };

  /**
   * Fusionne les points avec les mêmes coordonnées en un seul point
   * Version optimisée avec meilleure performance et fusion plus intelligente
   * @param points - Liste des points GeoJSON à traiter
   * @param tolerance - Tolérance en degrés pour considérer des coordonnées comme identiques (défaut: 0.0001 = ~11m)
   * @returns Liste des points fusionnés
   */
  const mergePointsWithSameCoordinates = (points: GeoJSONFeature[], tolerance?: number): GeoJSONFeature[] => {
    // Utiliser une tolérance plus agressive pour éliminer tous les doublons
    const optimalTolerance = tolerance ?? Math.max(0.00001, calculateOptimalTolerance(points) * 2);
    if (points.length === 0) return points;

    const mergedPoints: GeoJSONFeature[] = [];
    const processedIndices = new Set<number>();

    console.log(`🔄 Début de la fusion optimisée des points: ${points.length} points à traiter`);

    // Utiliser une Map pour un accès plus rapide aux coordonnées
    const coordinateMap = new Map<string, { point: GeoJSONFeature; index: number }[]>();

    // Pré-calculer les clés de coordonnées pour éviter les recalculs
    points.forEach((point, index) => {
      const coords = point.geometry.coordinates as number[];
      const lng = coords[0];
      const lat = coords[1];
      
      // Créer une clé basée sur la tolérance pour grouper les coordonnées similaires
      const key = `${Math.round(lng / optimalTolerance)}_${Math.round(lat / optimalTolerance)}`;
      
      if (!coordinateMap.has(key)) {
        coordinateMap.set(key, []);
      }
      coordinateMap.get(key)!.push({ point, index });
    });

    // Traiter chaque groupe de coordonnées similaires
    coordinateMap.forEach((group, key) => {
      if (group.length === 1) {
        // Point unique, l'ajouter tel quel
        mergedPoints.push(group[0].point);
        return;
      }

      // Grouper les points par coordonnées exactes dans la tolérance
      const exactGroups: { point: GeoJSONFeature; index: number }[][] = [];
      
      group.forEach(({ point, index }) => {
        if (processedIndices.has(index)) return;
        
        const coords = point.geometry.coordinates as number[];
        const lng = coords[0];
        const lat = coords[1];
        
        // Trouver un groupe existant avec des coordonnées similaires
        let foundGroup = false;
        for (const exactGroup of exactGroups) {
          const referenceCoords = exactGroup[0].point.geometry.coordinates as number[];
          const refLng = referenceCoords[0];
          const refLat = referenceCoords[1];
          
          const lngDiff = Math.abs(lng - refLng);
          const latDiff = Math.abs(lat - refLat);
          
          if (lngDiff <= optimalTolerance && latDiff <= optimalTolerance) {
            exactGroup.push({ point, index });
            foundGroup = true;
            break;
          }
        }
        
        if (!foundGroup) {
          exactGroups.push([{ point, index }]);
        }
      });

      // Fusionner chaque groupe de points similaires
      exactGroups.forEach(similarPoints => {
        if (similarPoints.length === 1) {
          mergedPoints.push(similarPoints[0].point);
          return;
        }

        // Marquer tous les points similaires comme traités
        similarPoints.forEach(({ index }) => {
          processedIndices.add(index);
        });

        const coords = similarPoints[0].point.geometry.coordinates as number[];
        const lng = coords[0];
        const lat = coords[1];
        
        console.log(`📍 Fusion de ${similarPoints.length} points aux coordonnées [${lng.toFixed(6)}, ${lat.toFixed(6)}]`);
        
        // Utiliser la fusion intelligente des propriétés
        const mergedProperties = mergePropertiesIntelligently(similarPoints.map(({ point }) => point));

        // Créer le point fusionné avec coordonnées moyennes si nécessaire
        const mergedPoint: GeoJSONFeature = {
          ...similarPoints[0].point,
          properties: mergedProperties
        };

        // Si les coordonnées ne sont pas exactement identiques, utiliser les coordonnées moyennes
        if (similarPoints.length > 1) {
          const allCoords = similarPoints.map(({ point }) => point.geometry.coordinates as number[]);
          const avgLng = allCoords.reduce((sum, coord) => sum + coord[0], 0) / allCoords.length;
          const avgLat = allCoords.reduce((sum, coord) => sum + coord[1], 0) / allCoords.length;
          
          mergedPoint.geometry = {
            ...mergedPoint.geometry,
            coordinates: [avgLng, avgLat]
          };
        }

        mergedPoints.push(mergedPoint);
      });
    });

    const fusionCount = points.length - mergedPoints.length;
    console.log(`✅ Fusion optimisée terminée: ${points.length} points → ${mergedPoints.length} points (${fusionCount} points fusionnés)`);
    
    if (fusionCount > 0) {
      console.log(`📊 Statistiques de fusion:`);
      console.log(`   - Points fusionnés: ${fusionCount}`);
      console.log(`   - Réduction: ${((fusionCount / points.length) * 100).toFixed(1)}%`);
      console.log(`   - Tolérance utilisée: ${optimalTolerance.toFixed(6)}° (~${Math.round(optimalTolerance * 111000)}m)`);
    }
    
    return mergedPoints;
  };

  /**
   * Fusionne les coordonnées identiques dans les LineString pour éviter les doublons
   * @param lineStrings - Liste des LineString GeoJSON à traiter
   * @param tolerance - Tolérance en degrés pour considérer des coordonnées comme identiques (défaut: 0.0001 = ~11m)
   * @returns Liste des LineString avec coordonnées fusionnées
   */
  const mergeLineStringCoordinates = (lineStrings: GeoJSONFeature[], tolerance: number = 0.0001): GeoJSONFeature[] => {
    return lineStrings.map(lineString => {
      const coords = lineString.geometry.coordinates as number[][];
      if (coords.length <= 1) return lineString;

      const mergedCoords: number[][] = [coords[0]]; // Toujours garder le premier point

      for (let i = 1; i < coords.length; i++) {
        const currentCoord = coords[i];
        const lastMergedCoord = mergedCoords[mergedCoords.length - 1];

        const lngDiff = Math.abs(currentCoord[0] - lastMergedCoord[0]);
        const latDiff = Math.abs(currentCoord[1] - lastMergedCoord[1]);

        // Si les coordonnées sont différentes (au-delà de la tolérance), les ajouter
        if (lngDiff > tolerance || latDiff > tolerance) {
          mergedCoords.push(currentCoord);
        }
      }

      // Si on a fusionné des coordonnées, créer un nouveau LineString
      if (mergedCoords.length !== coords.length) {
        console.log(`🔄 LineString: ${coords.length} → ${mergedCoords.length} coordonnées (${coords.length - mergedCoords.length} doublons supprimés)`);
        return {
          ...lineString,
          geometry: {
            ...lineString.geometry,
            coordinates: mergedCoords
          }
        };
      }

      return lineString;
    });
  };


  const findClosestElement = (lat: number, lng: number, maxDistance: number = 0.1): GridElement | null => {
    let closestElement: GridElement | null = null;
    let minDistance = maxDistance;

    console.log(`🔍 Searching for element near [${lat}, ${lng}] among ${existingElements.length} elements`);
    
    existingElements.forEach((element, index) => {
      if (element.gpsCoordinates) {
        const distance = calculateDistance(
          lat, lng,
          element.gpsCoordinates.latitude, element.gpsCoordinates.longitude
        );
        
        console.log(`📍 Element ${index} (${element.name}): [${element.gpsCoordinates.latitude}, ${element.gpsCoordinates.longitude}] - distance: ${distance.toFixed(4)}km`);
        
        if (distance < minDistance) {
          minDistance = distance;
          closestElement = element;
        }
      } else {
        console.warn(`⚠️ Element ${index} (${element.name}) has no GPS coordinates!`);
      }
    });

    if (closestElement) {
      console.log(`✅ Found closest element: ${(closestElement as GridElement).name} at distance ${minDistance.toFixed(4)}km`);
    } else {
      console.warn(`❌ No element found within ${maxDistance}km`);
    }

    return closestElement;
  };

  const getDefaultProperties = (type: string) => {
    switch (type) {
      case 'power-plant':
        return { power: 50, voltage: 400, efficiency: 85 };
      case 'meter':
        return { consumption: 2.5, voltage: 230 };
      case 'transformer':
        return { power: 25, voltage: 400 };
      default:
        return { voltage: 400 };
    }
  };

  // ===========================================================================
  // Import réseau amélioré : 1 élément par coordonnée exacte (dédup des features
  // empilées par layer), classification sémantique, et récupération du numéro de
  // poteau depuis les propriétés (key Sicame typicalNode_NN incluse).
  // ===========================================================================

  type NetworkClass =
    | 'power-plant' | 'transformer' | 'pole' | 'meter' | 'lighting' | 'equipment' | 'other';

  const CLASS_PRIORITY: Record<NetworkClass, number> = {
    'power-plant': 6, transformer: 5, pole: 4, meter: 3, lighting: 2, equipment: 1, other: 0,
  };

  const ELEMENT_TYPE_BY_CLASS: Record<NetworkClass, 'power-plant' | 'pole' | 'meter' | 'transformer'> = {
    'power-plant': 'power-plant', transformer: 'transformer', pole: 'pole',
    meter: 'meter', lighting: 'pole', equipment: 'pole', other: 'pole',
  };

  const TYPE_LABEL: Record<string, string> = {
    'power-plant': 'Centrale', transformer: 'Transformateur', meter: 'Compteur', pole: 'Poteau',
  };

  // Champs porteurs d'un identifiant lisible (numéro de poteau), par préférence.
  const NAME_FIELDS = [
    'name', 'pole_number', 'poleNumber', 'poteau_number', 'numero', 'number',
    'ref', 'reference', 'label', 'code', 'tag', 'title',
  ];

  // Clé de coordonnée arrondie (~0.11 m) pour regrouper les features empilées
  // exactement au même point sans fusionner des poteaux réellement distincts.
  const COORD_DECIMALS = 6;
  const coordKey = (lng: number, lat: number): string =>
    `${lng.toFixed(COORD_DECIMALS)},${lat.toFixed(COORD_DECIMALS)}`;

  const getLayerName = (props: Record<string, unknown> | undefined): string => {
    if (!props) return '';
    const layer = props.layer as any;
    if (layer && typeof layer === 'object') return String(layer.name ?? '');
    return String(layer ?? '');
  };

  // Classe sémantique d'un feature à partir du layer puis de properties.type.
  const classifyFeature = (props: Record<string, unknown> | undefined): NetworkClass => {
    const layer = getLayerName(props).toLowerCase();
    const t = String(props?.type ?? '').toLowerCase();
    const hay = `${layer} ${t}`;
    if (hay.includes('transfo')) return 'transformer';
    if (hay.includes('power-plant') || hay.includes('powerplant') || hay.includes('generator') ||
        hay.includes('centrale') || hay.includes('substation')) return 'power-plant';
    if (hay.includes('consumer') || hay.includes('compteur') || hay.includes('meter') ||
        hay.includes('client')) return 'meter';
    if (/(^|[^a-z])ep([^a-z]|$)/.test(layer) || hay.includes('éclair') || hay.includes('eclair') ||
        hay.includes('lighting') || hay.includes('lamp') || hay.includes('luminaire')) return 'lighting';
    if (hay.includes('support') || hay.includes('pole') || hay.includes('poteau')) return 'pole';
    if (hay.includes('fixation') || hay.includes('protection')) return 'equipment';
    return 'other';
  };

  // Identité (nom + numéro) d'un feature : champs lisibles, sinon clé Sicame.
  const extractIdentity = (
    props: Record<string, unknown> | undefined,
  ): { name?: string; number?: string; sourceKey?: string } => {
    if (!props) return {};
    for (const f of NAME_FIELDS) {
      const v = props[f];
      if (v != null && typeof v !== 'object') {
        const s = String(v).trim();
        if (s && s.toLowerCase() !== 'null' && s.toLowerCase() !== 'undefined') {
          // Premier nombre du libellé, aligné sur le backend poleNumberOf et le
          // matcher front resolvePoleByNumber ("Poteau 17-55" -> 17).
          const m = /\d+/.exec(s);
          return { name: s, number: m ? m[0] : undefined };
        }
      }
    }
    const key = props.key;
    if (key != null && typeof key !== 'object') {
      const s = String(key).trim();
      const m = /\d+/.exec(s);
      return { sourceKey: s, number: m ? m[0] : undefined };
    }
    return {};
  };

  /**
   * Construit un élément unique par coordonnée à partir des Points GeoJSON.
   * Les features empilées au même point (support + équipements + éclairage +
   * compteurs) sont fusionnées en un seul élément dont le type suit la priorité.
   */
  const buildElementsFromPoints = (points: GeoJSONFeature[]) => {
    const groups = new Map<string, GeoJSONFeature[]>();
    points.forEach((p) => {
      const c = p.geometry?.coordinates as number[];
      if (!Array.isArray(c) || c.length < 2 || !Number.isFinite(c[0]) || !Number.isFinite(c[1])) return;
      const k = coordKey(c[0], c[1]);
      if (!groups.has(k)) groups.set(k, []);
      groups.get(k)!.push(p);
    });

    const elements: GridElement[] = [];
    const coordIndex = new Map<string, GridElement>();
    const stats = {
      poles: 0, meters: 0, transformers: 0, powerPlants: 0,
      lightingAbsorbed: 0, equipmentAbsorbed: 0, consumersAttached: 0, mergedFeatures: 0,
    };

    const stamp = Date.now();
    let i = 0;
    groups.forEach((group) => {
      const classified = group.map((f) => ({ f, cls: classifyFeature(f.properties) }));
      let primary = classified[0];
      classified.forEach((c) => {
        if (CLASS_PRIORITY[c.cls] > CLASS_PRIORITY[primary.cls]) primary = c;
      });

      const elementType = ELEMENT_TYPE_BY_CLASS[primary.cls];
      const coords = primary.f.geometry.coordinates as number[];

      // Identité : feature primaire, sinon n'importe quel feature porteur d'un numéro.
      let identity = extractIdentity(primary.f.properties);
      if (!identity.name && !identity.number && !identity.sourceKey) {
        for (const c of classified) {
          const id = extractIdentity(c.f.properties);
          if (id.name || id.number || id.sourceKey) { identity = id; break; }
        }
      }
      const label = TYPE_LABEL[elementType] ?? 'Élément';
      const name = identity.name ?? `${label} ${identity.number ?? i + 1}`;

      const classCounts: Record<string, number> = {};
      const sourceKeys: string[] = [];
      classified.forEach((c) => {
        classCounts[c.cls] = (classCounts[c.cls] ?? 0) + 1;
        const sk = c.f.properties?.key;
        if (sk != null && typeof sk !== 'object') sourceKeys.push(String(sk));
      });
      const consumerCount = classCounts['meter'] ?? 0;

      stats.mergedFeatures += group.length - 1;
      if (elementType === 'pole') stats.poles += 1;
      else if (elementType === 'meter') stats.meters += 1;
      else if (elementType === 'transformer') stats.transformers += 1;
      else if (elementType === 'power-plant') stats.powerPlants += 1;
      stats.lightingAbsorbed += classCounts['lighting'] ?? 0;
      stats.equipmentAbsorbed += classCounts['equipment'] ?? 0;
      if (elementType !== 'meter') stats.consumersAttached += consumerCount;

      const grid = geoToGridCoordinates(coords[0], coords[1]);
      const element: GridElement = {
        id: `imported_${stamp}_${i}`,
        type: elementType,
        x: grid.x,
        y: grid.y,
        name,
        number: identity.number,
        properties: {
          ...getDefaultProperties(elementType),
          ...primary.f.properties,
          sourceKeys,
          importedClassCounts: classCounts,
          consumerCount,
        },
        status: 'ACTIVE',
        lights: elementType === 'pole' ? [] : undefined,
        gpsCoordinates: { latitude: coords[1], longitude: coords[0] },
        action: ElementAction.TO_CREATE,
      };
      elements.push(element);
      coordIndex.set(coordKey(coords[0], coords[1]), element);
      i += 1;
    });

    return { elements, coordIndex, stats };
  };

  /**
   * Construit les connexions à partir des LineString en rattachant chaque sommet
   * à l'élément déjà créé à cette coordonnée (snap exact, sinon plus proche < 5 m,
   * sinon création d'un poteau de jonction). Évite ainsi de recréer des poteaux
   * en doublon sur les sommets de lignes.
   */
  const buildConnectionsFromLineStrings = (
    lineStrings: GeoJSONFeature[],
    coordIndex: Map<string, GridElement>,
  ) => {
    const extraElements: GridElement[] = [];
    const connections: Connection[] = [];
    const edgeSet = new Set<string>();
    const indexed = Array.from(coordIndex.values());
    const stamp = Date.now();

    const resolveNode = (lng: number, lat: number, lineIndex: number): GridElement => {
      const k = coordKey(lng, lat);
      const existing = coordIndex.get(k);
      if (existing) return existing;

      let best: GridElement | null = null;
      let bestD = Infinity;
      indexed.forEach((el) => {
        if (!el.gpsCoordinates) return;
        const d = calculateDistance(lat, lng, el.gpsCoordinates.latitude, el.gpsCoordinates.longitude);
        if (d < bestD) { bestD = d; best = el; }
      });
      if (best && bestD <= 0.005) return best; // snap < 5 m

      const grid = geoToGridCoordinates(lng, lat);
      const node: GridElement = {
        id: `imported_node_${stamp}_${extraElements.length}`,
        type: 'pole',
        x: grid.x,
        y: grid.y,
        name: `Poteau (jonction) ${lineIndex + 1}`,
        properties: { ...getDefaultProperties('pole'), autoCreatedFromLine: true },
        status: 'ACTIVE',
        lights: [],
        gpsCoordinates: { latitude: lat, longitude: lng },
        action: ElementAction.TO_CREATE,
      };
      extraElements.push(node);
      coordIndex.set(k, node);
      indexed.push(node);
      return node;
    };

    lineStrings.forEach((ls, index) => {
      const coords = ls.geometry?.coordinates as number[][];
      if (!Array.isArray(coords) || coords.length < 2) return;
      const connectionType = ls.properties?.type === 'data-line' ? 'data-line' : 'power-line';

      for (let s = 0; s < coords.length - 1; s++) {
        const a = coords[s];
        const b = coords[s + 1];
        if (!Array.isArray(a) || !Array.isArray(b) || a.length < 2 || b.length < 2) continue;
        if (![a[0], a[1], b[0], b[1]].every((n) => Number.isFinite(n))) continue;
        const from = resolveNode(a[0], a[1], index);
        const to = resolveNode(b[0], b[1], index);
        if (from.id === to.id) continue; // segment dégénéré

        const edgeKey = [from.id, to.id].sort().join('|');
        if (edgeSet.has(edgeKey)) continue; // arête déjà créée
        edgeSet.add(edgeKey);

        const distance = calculateDistance(
          from.gpsCoordinates!.latitude, from.gpsCoordinates!.longitude,
          to.gpsCoordinates!.latitude, to.gpsCoordinates!.longitude,
        );
        connections.push({
          id: `imported_connection_${stamp}_${index}_${s}`,
          fromId: from.id,
          toId: to.id,
          type: connectionType as 'power-line' | 'data-line',
          voltage: connectionType === 'power-line' ? 400 : 0,
          resistance: connectionType === 'power-line' ? 0.2 : 0.1,
          length: distance,
          status: 'ACTIVE',
          action: ElementAction.TO_CREATE,
        });
      }
    });

    return { extraElements, connections };
  };

  /**
   * Rend le réseau radial : conserve un arbre couvrant (par composante connexe)
   * et retire les câbles qui referment une boucle. On trie par longueur croissante
   * (Kruskal) afin de couper le câble le plus long de chaque boucle. La
   * connectivité du réseau est préservée (mêmes composantes, sans cycle).
   */
  const radializeConnections = (
    conns: Connection[],
  ): { kept: Connection[]; removed: Connection[] } => {
    const parent = new Map<string, string>();
    const ensure = (x: string) => { if (!parent.has(x)) parent.set(x, x); };
    const find = (x: string): string => {
      let root = x;
      while (parent.get(root) !== root) root = parent.get(root)!;
      let cur = x;
      while (parent.get(cur) !== root) { const next = parent.get(cur)!; parent.set(cur, root); cur = next; }
      return root;
    };

    const kept: Connection[] = [];
    const removed: Connection[] = [];
    [...conns]
      .sort((a, b) => (a.length ?? 0) - (b.length ?? 0))
      .forEach((c) => {
        ensure(c.fromId);
        ensure(c.toId);
        const ra = find(c.fromId);
        const rb = find(c.toId);
        if (ra === rb) {
          removed.push(c); // referme une boucle → retiré
          return;
        }
        parent.set(ra, rb);
        kept.push(c);
      });
    return { kept, removed };
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const geoJSON = JSON.parse(e.target?.result as string);
        
        // Validation basique du GeoJSON
        if (!geoJSON.type || (geoJSON.type !== 'FeatureCollection' && geoJSON.type !== 'Feature')) {
          alert('Le fichier n\'est pas un GeoJSON valide');
          return;
        }

        // Extraction des géométries
        const rawPoints = extractPoints(geoJSON);
        const rawLineStrings = extractLineStrings(geoJSON);

        console.log(`📊 GeoJSON chargé: ${rawPoints.length} points, ${rawLineStrings.length} linestrings`);

        // 1) Un élément unique par coordonnée (dédup des features empilées) + numéro de poteau
        const { elements: pointElements, coordIndex, stats } = buildElementsFromPoints(rawPoints);

        // 2) Connexions à partir des lignes, sommets snappés aux éléments existants
        const { extraElements, connections } = buildConnectionsFromLineStrings(rawLineStrings, coordIndex);

        // 3) Réseau radial optionnel : retirer les câbles qui referment une boucle.
        const { kept: radialConnections, removed: removedLoops } = radializeConnections(connections);
        const loopCount = removedLoops.length;
        const finalConnections = radializeNetwork ? radialConnections : connections;

        // Élaguer les jonctions auto-créées (autoCreatedFromLine) restées sans aucune
        // connexion dans le réseau final : segment dégénéré (a==b) à une coordonnée
        // neuve, ou unique câble retiré. On ne touche jamais aux éléments issus des
        // Points (poteaux/compteurs réels), seulement aux jonctions synthétiques.
        const connectedIds = new Set<string>();
        finalConnections.forEach((c) => { connectedIds.add(c.fromId); connectedIds.add(c.toId); });
        const keptExtra = extraElements.filter((el) => connectedIds.has(el.id));
        const allElements = [...pointElements, ...keptExtra];

        if (allElements.length > 0 && onElementsAdd) {
          onElementsAdd(allElements);
        }
        if (finalConnections.length > 0 && onConnectionsAdd) {
          onConnectionsAdd(finalConnections);
        }

        // Ajouter comme couche pour l'affichage des autres géométries
        addAsLayer(geoJSON, file.name);

        // Message de confirmation détaillé
        if (allElements.length > 0 || finalConnections.length > 0) {
          const parts: string[] = [];
          if (stats.poles > 0) parts.push(`${stats.poles} poteaux`);
          if (stats.transformers > 0) parts.push(`${stats.transformers} transfo`);
          if (stats.powerPlants > 0) parts.push(`${stats.powerPlants} centrale(s)`);
          if (stats.meters > 0) parts.push(`${stats.meters} compteurs`);
          if (keptExtra.length > 0) parts.push(`${keptExtra.length} jonctions auto`);

          let message = `✅ Import réussi !\n\n`;
          message += `📍 ${allElements.length} éléments créés (${parts.join(', ')})\n`;
          message += `🔗 ${finalConnections.length} connexions créées\n`;
          if (stats.mergedFeatures > 0) {
            message += `🧹 ${stats.mergedFeatures} features empilées fusionnées (doublons supprimés)\n`;
          }
          if (stats.consumersAttached > 0) {
            message += `👥 ${stats.consumersAttached} compteurs rattachés aux poteaux\n`;
          }
          if (stats.lightingAbsorbed > 0) {
            message += `💡 ${stats.lightingAbsorbed} points d'éclairage public intégrés\n`;
          }
          if (loopCount > 0) {
            if (radializeNetwork) {
              const sample = removedLoops
                .slice(0, 5)
                .map((c) => {
                  const a = allElements.find((e) => e.id === c.fromId)?.name ?? c.fromId;
                  const b = allElements.find((e) => e.id === c.toId)?.name ?? c.toId;
                  return `${a} ⟷ ${b}`;
                })
                .join(', ');
              message += `🔁 ${loopCount} boucle(s) ouverte(s) → réseau radial`;
              message += sample ? ` (ex: ${sample}${loopCount > 5 ? '…' : ''})\n` : `\n`;
            } else {
              message += `🔁 ${loopCount} boucle(s) détectée(s), conservées (import fidèle)\n`;
            }
          }
          message += `\nLe fichier "${file.name}" a été importé avec succès.`;
          alert(message);
        } else {
          alert(`✅ Import réussi !\n\nLe fichier "${file.name}" a été ajouté comme couche.`);
        }
      } catch (error) {
        alert('Erreur lors de la lecture du fichier GeoJSON');
        console.error('GeoJSON parse error:', error);
      }
    };

    reader.readAsText(file);
    
    // Reset l'input pour permettre de sélectionner le même fichier à nouveau
    if (event.target) {
      event.target.value = '';
    }
  };

  const convertPointsToElements = () => {
    if (!selectedFile || !onElementsAdd) return;

    const rawPoints = extractPoints(selectedFile.geoJSON);
    const points = mergePointsWithSameCoordinates(rawPoints);
    const elements: GridElement[] = [];

    points.forEach((point, index) => {
      const coords = point.geometry.coordinates as number[];
      const gridCoords = geoToGridCoordinates(coords[0], coords[1]);
      const pointId = point.id != null ? String(point.id) : `point_${index}`;
      const elementType = pointMapping[pointId] || 'pole';
      const identity = extractIdentity(point.properties);
      const typeLabel = TYPE_LABEL[elementType] ?? 'Élément';

      const newElement: GridElement = {
        id: `imported_${Date.now()}_${index}`,
        type: elementType as 'power-plant' | 'pole' | 'meter' | 'transformer',
        x: gridCoords.x,
        y: gridCoords.y,
        name: identity.name ?? `${typeLabel} ${identity.number ?? index + 1}`,
        number: identity.number,
        properties: {
          ...getDefaultProperties(elementType),
          ...point.properties // Conserver les propriétés originales
        },
        status: 'ACTIVE',
        lights: elementType === 'pole' ? [] : undefined,
        // Ajouter les coordonnées GPS originales
        gpsCoordinates: {
          latitude: coords[1], // latitude
          longitude: coords[0] // longitude
        },
        // Marquer pour création lors de la sauvegarde
        action: ElementAction.TO_CREATE
      };

      elements.push(newElement);
    });

    onElementsAdd(elements);
    
    // Aussi ajouter comme couche GeoJSON pour les autres géométries
    addAsLayer(selectedFile.geoJSON, selectedFile.fileName);
    
    // Fermer le convertisseur
    setShowPointConverter(false);
    setSelectedFile(null);
    setPointMapping({});
  };

  const testConnectionCreation = () => {
    if (!onConnectionsAdd || existingElements.length < 2) {
      console.warn('❌ Cannot test connection creation: insufficient elements or missing callback');
      return;
    }

    console.log('🧪 Testing connection creation...');
    
    const testConnection: Connection = {
      id: `test_connection_${Date.now()}`,
      fromId: existingElements[0].id,
      toId: existingElements[1].id,
      type: 'power-line',
      voltage: 400,
      resistance: 0.2,
      length: 0.1,
      status: 'ACTIVE'
    };

    console.log('🧪 Creating test connection:', testConnection);
    onConnectionsAdd([testConnection]);
    console.log('🧪 Test connection created!');
  };

  const convertLineStringsToConnectionsAutomatically = (lineStrings: any[]) => {
    if (!onConnectionsAdd || !onElementsAdd) {
      console.warn('❌ onConnectionsAdd or onElementsAdd function not provided');
      return;
    }

    console.log(`🔄 Starting automatic conversion of ${lineStrings.length} LineStrings`);
    console.log(`📍 Available elements: ${existingElements.length}`);
    
    const elements: GridElement[] = [];
    const connections: Connection[] = [];

    lineStrings.forEach((lineString, index) => {
      const coords = lineString.geometry.coordinates as number[][];
      
      // Déterminer le type de connexion automatiquement
      const connectionType = lineString.properties?.type === 'data-line' ? 'data-line' : 'power-line';
      
      console.log(`🔄 Processing LineString ${index} automatically:`, {
        coords: coords.length,
        type: connectionType,
        firstCoord: coords[0],
        lastCoord: coords[coords.length - 1],
        properties: lineString.properties
      });
      
      // Créer des éléments pour chaque point du LineString
      const lineStringElements: GridElement[] = [];
      
      coords.forEach((coord, coordIndex) => {
        const lng = coord[0];
        const lat = coord[1];
        
        // Convertir les coordonnées GPS en coordonnées canvas
        const gridCoords = geoToGridCoordinates(lng, lat);
        
        const elementId = `linestring_${index}_point_${coordIndex}`;
        const elementName = `${connectionType === 'power-line' ? 'Poteau' : 'Point'} ${index + 1}-${coordIndex + 1}`;
        
        const newElement: GridElement = {
          id: elementId,
          type: 'pole', // Par défaut, créer des poteaux
          x: gridCoords.x,
          y: gridCoords.y,
          name: elementName,
          properties: {
            voltage: connectionType === 'power-line' ? 400 : 0
          },
          status: 'ACTIVE',
          lights: [],
          // Ajouter les coordonnées GPS originales
          gpsCoordinates: {
            latitude: lat,
            longitude: lng
          },
          // Marquer pour création lors de la sauvegarde
          action: ElementAction.TO_CREATE
        };
        
        lineStringElements.push(newElement);
        elements.push(newElement);
        
        console.log(`📍 Created element ${coordIndex}:`, {
          id: elementId,
          name: elementName,
          coords: [lng, lat],
          gridCoords: [gridCoords.x, gridCoords.y]
        });
      });
      
      // Créer des connexions entre les éléments consécutifs
      for (let i = 0; i < lineStringElements.length - 1; i++) {
        const startElement = lineStringElements[i];
        const endElement = lineStringElements[i + 1];
        
        // Calculer la distance entre les éléments
        const distance = calculateDistance(
          startElement.gpsCoordinates!.latitude, startElement.gpsCoordinates!.longitude,
          endElement.gpsCoordinates!.latitude, endElement.gpsCoordinates!.longitude
        );
        
        const newConnection: Connection = {
          id: `imported_connection_${Date.now()}_${index}_${i}`,
          fromId: startElement.id,
          toId: endElement.id,
          type: connectionType as 'power-line' | 'data-line',
          voltage: connectionType === 'power-line' ? 400 : 0,
          resistance: connectionType === 'power-line' ? 0.2 : 0.1,
          length: distance,
          status: 'ACTIVE',
          // Marquer pour création lors de la sauvegarde
          action: ElementAction.TO_CREATE
        };

        connections.push(newConnection);
        
        console.log(`✅ Created connection:`, {
          from: startElement.name,
          to: endElement.name,
          distance: `${distance.toFixed(3)}km`,
          type: connectionType,
          connectionId: newConnection.id
        });
      }
    });

    console.log(`📊 Conversion complete: ${elements.length} elements and ${connections.length} connections created`);
    
    // Ajouter les éléments d'abord
    if (elements.length > 0) {
      console.log('🎉 Calling onElementsAdd with elements:', elements);
      onElementsAdd(elements);
    }
    
    // Puis ajouter les connexions
    if (connections.length > 0) {
      console.log('🎉 Calling onConnectionsAdd with connections:', connections);
      onConnectionsAdd(connections);
      console.log(`🎉 Created ${elements.length} elements and ${connections.length} connections automatically from LineStrings`);
    } else {
      console.warn('⚠️ No connections could be created automatically.');
    }
  };

  const convertLineStringsToConnections = () => {
    if (!selectedFile || !onConnectionsAdd) return;

    const rawLineStrings = extractLineStrings(selectedFile.geoJSON);
    const lineStrings = mergeLineStringCoordinates(rawLineStrings);
    const connections: Connection[] = [];

    lineStrings.forEach((lineString, index) => {
      const coords = lineString.geometry.coordinates as number[][];
      const lineStringId = String(lineString.id) || `linestring_${index}`;
      const connectionType = lineStringMapping[lineStringId] || 'power-line';
      
      console.log(`🔄 Processing LineString ${index}:`, {
        coords: coords.length,
        type: connectionType,
        firstCoord: coords[0],
        lastCoord: coords[coords.length - 1]
      });
      
      // Pour chaque paire de coordonnées consécutives, créer une connexion
      for (let i = 0; i < coords.length - 1; i++) {
        const startCoords = coords[i];
        const endCoords = coords[i + 1];
        
        // Vérifier l'ordre des coordonnées : [longitude, latitude]
        const startLng = startCoords[0];
        const startLat = startCoords[1];
        const endLng = endCoords[0];
        const endLat = endCoords[1];
        
        console.log(`📍 Segment ${i}:`, {
          start: [startLng, startLat],
          end: [endLng, endLat]
        });
        
        // Trouver les éléments les plus proches des points de début et fin
        const startElement = findClosestElement(startLat, startLng, 0.05); // 50m de tolérance
        const endElement = findClosestElement(endLat, endLng, 0.05);
        
        if (startElement && endElement && startElement.id !== endElement.id) {
          // Calculer la distance entre les éléments
          const distance = calculateDistance(
            startElement.gpsCoordinates!.latitude, startElement.gpsCoordinates!.longitude,
            endElement.gpsCoordinates!.latitude, endElement.gpsCoordinates!.longitude
          );
          
          const newConnection: Connection = {
            id: `imported_connection_${Date.now()}_${index}_${i}`,
            fromId: startElement.id,
            toId: endElement.id,
            type: connectionType as 'power-line' | 'data-line',
            voltage: connectionType === 'power-line' ? 400 : 0,
            resistance: connectionType === 'power-line' ? 0.2 : 0.1,
            length: distance,
            status: 'ACTIVE'
          };

          connections.push(newConnection);
          
          console.log(`✅ Created connection:`, {
            from: startElement.name,
            to: endElement.name,
            distance: `${distance.toFixed(3)}km`,
            type: connectionType
          });
        } else {
          console.warn(`⚠️ No valid elements found for segment ${i}:`, {
            startElement: startElement?.name || 'not found',
            endElement: endElement?.name || 'not found',
            startCoords: [startLng, startLat],
            endCoords: [endLng, endLat]
          });
        }
      }
    });

    if (connections.length > 0) {
      onConnectionsAdd(connections);
      console.log(`🎉 Created ${connections.length} connections from LineStrings`);
    } else {
      console.warn('⚠️ No connections could be created. Make sure elements exist near LineString coordinates.');
    }
    
    // Fermer le convertisseur
    setShowLineStringConverter(false);
    setSelectedFile(null);
    setLineStringMapping({});
  };

  const cancelConversion = () => {
    if (selectedFile) {
      // Ajouter simplement comme couche normale
      addAsLayer(selectedFile.geoJSON, selectedFile.fileName);
    }
    
    // Fermer tous les convertisseurs
    setShowPointConverter(false);
    setShowLineStringConverter(false);
    setSelectedFile(null);
    setPointMapping({});
    setLineStringMapping({});
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const state: GeoJSONImporterState = {
    showPointConverter,
    showLineStringConverter,
    selectedFile,
    pointMapping,
    lineStringMapping,
    radializeNetwork
  };

  const actions: GeoJSONImporterActions = {
    handleFileSelect,
    convertPointsToElements,
    convertLineStringsToConnections,
    convertLineStringsToConnectionsAutomatically,
    testConnectionCreation,
    cancelConversion,
    setPointMapping,
    setLineStringMapping,
    setRadializeNetwork,
    triggerFileInput
  };

  return {
    state,
    actions,
    fileInputRef,
    extractPoints,
    extractLineStrings
  };
};
